import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import OrderActivity from "@/components/orders/OrderActivity";
import { DatePicker } from "@/components/ui/date-picker";
import { useNavigate } from "react-router-dom";
import {
  ArrowDown, ArrowUp, ArrowUpDown, ChevronDown, ChevronLeft, ChevronRight, Download, Eye,
  FileText, PencilLine, Printer, ReceiptText, Search, SlidersHorizontal, Trash2, X,
} from "lucide-react";
import { toast } from "sonner";
import { usePOS } from "@/context/POSContext";
import * as history from "@/lib/orderHistoryApi";
import { pdfBlob } from "@/lib/simplePdf";
import { fmtQty } from "@/lib/qty";
import { MONEY_LINE, inr } from "@/lib/format";
import { PageShell } from "@/components/PageShell";
import { EmptyState, ErrorState, LoadingState } from "@/components/ui/StateBoundaries";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import ChangePaymentDialog from "@/components/billing/ChangePaymentDialog";
import { featureAllowed } from "@/lib/access";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/**
 * Orders/KOT History — two ledgers under one search panel.
 *
 * Owner's vocabulary (2026-08-26): BILL NUMBER and ORDER NUMBER are the same
 * thing; the system calls it bill number. It is stamped when a table's first
 * KOT is punched and is never reused — billed, voided or deleted.
 *
 *   Orders — every bill: generated, settled, or voided after the kitchen fired.
 *   KOT    — every punched KOT regardless of the order's status.
 *
 * Every control here does something real (§45), and it is drawn only when
 * this session can use it — `abilities` come back with each ledger from the
 * server's own permission check, and the server re-checks on every action:
 *   View    — the bill's lines, money and tenders, in a drawer (§5.3).
 *   Print   — re-queues the bill / that KOT at its printers (`orders.reprint`).
 *   Edit    — a live order opens in the till; a finished bill is first
 *             reopened by admin override (`orders.override`), audited.
 *   Delete  — admin override: the bill is voided with a mandatory reason;
 *             its number stays burned (§11) and the row stays on the ledger.
 *
 * Screen grammar is the reference screens' (DESIGN.md): one dense filter
 * row, one primary action, underline tabs, a scrolling table at row density,
 * money in MONEY_LINE, status as a badge, states from StateBoundaries.
 */

const PAGE_SIZE = 100;
const ACTIVE_STATUSES = new Set(["open", "kot_sent", "billing"]);
const EMPTY_FILTERS = { start: "", end: "", number: "", tableNumber: "", paymentType: "" };
const EMPTY_SORT = { orders: { by: null, dir: "desc" }, kots: { by: null, dir: "desc" } };

const TABS = [
  ["orders", "Orders", ReceiptText],
  ["kots", "KOT", FileText],
];

/* ---------------------------------------------------------------- helpers */

export const fmtTime = iso => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const day = d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
  // hourCycle, not hour12:false — the latter renders midnight as "24:xx".
  const time = d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hourCycle: "h23" });
  return `${day} ${time}`;
};

const isActive = status => ACTIVE_STATUSES.has(status);

const paymentLabel = value => {
  if (!value) return "—";
  const text = String(value);
  return text.startsWith("split:") ? `Split · ${text.slice(6).trim().replace(/\+/g, " + ")}` : text;
};

const detailOf = error => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string" && detail) return detail;
  if (detail?.message) return detail.message;
  if (error?.response?.status === 403) return "This account is not allowed to do that.";
  if (!error?.response || [502, 503, 504].includes(error.response.status)) return "The server is not answering. Check that the server PC is on and connected.";
  return "The server rejected the request.";
};

const escapeHtml = value =>
  String(value ?? "").replace(/[&<>"']/g, ch => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ch]));

const toCsv = rows => {
  if (!rows.length) return "";
  const headers = Object.keys(rows[0]);
  const cell = v => `"${String(v ?? "").replaceAll('"', '""')}"`;
  // BOM so Excel reads the ₹ and the accents as UTF-8.
  return "﻿" + [headers.join(","), ...rows.map(r => headers.map(h => cell(r[h])).join(","))].join("\n");
};

const download = (content, filename, mime) => {
  const blob = content instanceof Blob ? content : new Blob([content], { type: mime || "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const printRows = (title, rows) => {
  if (!rows.length) return false;
  const headers = Object.keys(rows[0]);
  const w = window.open("", "_blank", "width=900,height=700");
  if (!w) return false;
  w.document.write(`<!doctype html><html><head><title>${escapeHtml(title)}</title><style>
    body{font-family:system-ui,sans-serif;font-size:11px;padding:16px;color:#111}
    h2{margin:0 0 8px;font-size:14px}
    table{border-collapse:collapse;width:100%}
    th,td{border:1px solid #ccc;padding:4px 6px;text-align:left;vertical-align:top}
    th{background:#f3f4f6;font-size:10px;text-transform:uppercase;letter-spacing:.04em}
  </style></head><body><h2>${escapeHtml(title)}</h2>
  <table><thead><tr>${headers.map(h => `<th>${escapeHtml(h)}</th>`).join("")}</tr></thead>
  <tbody>${rows.map(r => `<tr>${headers.map(h => `<td>${escapeHtml(r[h])}</td>`).join("")}</tr>`).join("")}</tbody>
  </table></body></html>`);
  w.document.close();
  w.focus();
  w.print();
  return true;
};

const billExportRow = b => ({
  "Bill Number": b.bill_number,
  Status: b.status,
  Table: b.table_display,
  "Customer Number": b.customer_phone || "",
  "Customer Name": b.customer_name || "",
  "Payment Type": paymentLabel(b.payment_type),
  // Plain figure, two decimals: Excel sums it, and the PDF's Helvetica has no ₹ glyph.
  "Total Amount": Number(b.total_amount ?? 0).toFixed(2),
  "Printed At": fmtTime(b.printed_at),
  "Settled Time": fmtTime(b.settled_time),
});

const kotExportRow = k => ({
  "KOT No": k.kot_number,
  "Bill Number": k.bill_number ?? "",
  Table: k.table_display,
  "Captain Name": k.captain_name,
  "KOT Time": fmtTime(k.kot_time),
  Items: k.items || "",
});

/* ---------------------------------------------------------------- page */

export default function OrderHistoryPage() {
  const [filtersOpen, setFiltersOpen] = useState(false);
  const { token, user, permissions } = usePOS();
  const navigate = useNavigate();

  const [tab, setTab] = useState("orders");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [applied, setApplied] = useState(EMPTY_FILTERS);
  const [sort, setSort] = useState(EMPTY_SORT);
  const [page, setPage] = useState({ orders: 0, kots: 0 });
  const [bills, setBills] = useState({ orders: [], total: 0, abilities: {} });
  const [kots, setKots] = useState({ kots: [], total: 0, abilities: {} });
  const [paymentModes, setPaymentModes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [voidTarget, setVoidTarget] = useState(null);
  const [reopenTarget, setReopenTarget] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const seq = useRef(0);

  const abilities = tab === "orders" ? bills.abilities || {} : kots.abilities || {};
  const canReprint = Boolean(abilities.reprint);
  const canOverride = Boolean(abilities.override);

  // The server's own filter vocabulary: everything the panel applied, in the
  // shape each ledger takes. Sort is per tab so switching does not re-sort
  // the other ledger behind the owner's back.
  const common = useMemo(() => {
    const p = {};
    if (applied.start) p.start = new Date(applied.start).toISOString();
    if (applied.end) p.end = new Date(applied.end).toISOString();
    if (applied.tableNumber) p.table_number = applied.tableNumber;
    return p;
  }, [applied]);

  const billParams = useMemo(() => {
    const p = { ...common, limit: PAGE_SIZE, offset: page.orders * PAGE_SIZE };
    if (applied.number) p.bill_number = applied.number;
    if (applied.paymentType) p.payment_type = applied.paymentType;
    if (sort.orders.by) { p.sort_by = sort.orders.by; p.direction = sort.orders.dir; }
    return p;
  }, [common, applied, sort.orders, page.orders]);

  const kotParams = useMemo(() => {
    const p = { ...common, limit: PAGE_SIZE, offset: page.kots * PAGE_SIZE };
    if (applied.number) p.kot_number = applied.number;
    if (sort.kots.by) { p.sort_by = sort.kots.by; p.direction = sort.kots.dir; }
    return p;
  }, [common, applied, sort.kots, page.kots]);

  const load = useCallback(async () => {
    const mine = (seq.current += 1);
    setLoading(true);
    try {
      // Both ledgers share the panel and the export menu, so both stay loaded;
      // the tabs only switch which one is on screen (and carry its count).
      const [b, k] = await Promise.all([
        history.listBillHistory(billParams, token),
        history.listKotHistory(kotParams, token),
      ]);
      if (seq.current !== mine) return;
      setBills({ orders: b?.orders || [], total: b?.total || 0, abilities: b?.abilities || {} });
      setKots({ kots: k?.kots || [], total: k?.total || 0, abilities: k?.abilities || {} });
      setError(null);
    } catch (failure) {
      if (seq.current !== mine) return;
      setError(detailOf(failure));
    } finally {
      if (seq.current === mine) setLoading(false);
    }
  }, [billParams, kotParams, token]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let live = true;
    history.listPaymentModes(token)
      .then(rows => { if (live) setPaymentModes((rows || []).filter(m => m.is_active !== false)); })
      .catch(() => { /* the filter simply offers "All" */ });
    return () => { live = false; };
  }, [token]);

  const apply = event => {
    event.preventDefault();
    setPage({ orders: 0, kots: 0 });
    setApplied(filters);
  };

  const clear = () => {
    setFilters(EMPTY_FILTERS);
    setApplied(EMPTY_FILTERS);
    setPage({ orders: 0, kots: 0 });
  };

  const onSort = field => setSort(s => {
    const current = s[tab];
    const next = current.by === field
      ? { by: field, dir: current.dir === "asc" ? "desc" : "asc" }
      : { by: field, dir: "desc" };
    return { ...s, [tab]: next };
  });

  const turnPage = delta => setPage(p => ({ ...p, [tab]: Math.max(0, p[tab] + delta) }));

  /* ---- actions (each gated on the server's abilities and re-checked there) */

  const reprint = async row => {
    if (busyId) return;
    setBusyId(row.id ?? `${row.order_id}-${row.kot_number}`);
    try {
      const payload = row.kot_number !== undefined
        ? await history.reprintKot(row.order_id, row.kot_number, token)
        : await history.reprintBill(row.id, token);
      const jobs = Array.isArray(payload?.job_ids) ? payload.job_ids.length : 0;
      if (jobs > 0) toast.success(`Re-queued · ${jobs} print job${jobs === 1 ? "" : "s"}`);
      else toast.error("No active printer accepted it — nothing was queued.");
    } catch (failure) {
      toast.error(detailOf(failure));
    } finally {
      setBusyId(null);
    }
  };

  const openInTill = row => {
    const orderId = row.id ?? row.order_id;
    if (!row.table_id) {
      toast.error("This order has no table — open it from the running orders board.");
      return;
    }
    navigate(`/billing/table/${row.table_id}?order=${orderId}`);
  };

  const edit = row => {
    if (isActive(row.status)) openInTill(row);
    else setReopenTarget(row);
  };

  const confirmReopen = async () => {
    const row = reopenTarget;
    setReopenTarget(null);
    if (!row) return;
    const orderId = row.id ?? row.order_id;
    try {
      await history.reopenOrder(orderId, token);
      toast.success(`Bill #${row.bill_number ?? row.order_number} reopened`);
      openInTill({ ...row, status: "open" });
    } catch (failure) {
      toast.error(detailOf(failure));
    }
  };

  const confirmVoid = async reason => {
    const row = voidTarget;
    setVoidTarget(null);
    if (!row) return;
    try {
      await history.voidOrder(row.id, reason, token);
      toast.success(`Bill #${row.bill_number} deleted — number ${row.bill_number} stays retired`);
      load();
    } catch (failure) {
      toast.error(detailOf(failure));
    }
  };

  /* ---- export */

  const exportSets = useMemo(() => ({
    bills: bills.orders.map(billExportRow),
    kots: kots.kots.map(kotExportRow),
  }), [bills, kots]);

  const doExport = (kind, mode) => {
    const rows = kind === "all"
      ? [...exportSets.bills, ...exportSets.kots]
      : exportSets[kind];
    if (!rows.length) {
      toast.error("Nothing to export in this window.");
      return;
    }
    const name = `restoyaar-${kind}-${new Date().toISOString().slice(0, 10)}`;
    if (mode === "csv") {
      download(toCsv(rows), `${name}.csv`);
      return;
    }
    if (mode === "pdf") {
      const fields = Object.keys(rows[0]).map(h => [h, r => String(r[h] ?? "")]);
      download(pdfBlob(rows, fields, `RestoYaar — ${name}`), `${name}.pdf`, "application/pdf");
      return;
    }
    if (!printRows(`RestoYaar — ${name}`, rows)) toast.error("Allow pop-ups for this site to print.");
  };

  /* ---- render */

  const rows = tab === "orders" ? bills.orders : kots.kots;
  const total = tab === "orders" ? bills.total : kots.total;
  const billActions = b => ({
    onView: () => setDetailId(b.id),
    onPrint: canReprint && b.status !== "cancelled" ? () => reprint(b) : null,
    onEdit: isActive(b.status) || canOverride ? () => edit(b) : null,
    editLabel: isActive(b.status) ? "Open in till" : "Reopen and edit (admin)",
    onDelete: canOverride && b.status !== "cancelled" ? () => setVoidTarget(b) : null,
  });
  const currentPage = page[tab];
  const currentSort = sort[tab];
  const from = total ? currentPage * PAGE_SIZE + 1 : 0;
  const to = Math.min(total, currentPage * PAGE_SIZE + rows.length);

  return (
    <PageShell testId="order-history-page">
      {/* Common search panel — one row, one primary action */}
      <form
        data-testid="history-search-panel"
        className="flex flex-wrap items-end gap-base shrink-0"
        onSubmit={apply}
      >
        {/* Phone: the filters filled the whole first screen (owner, 2026-10-25). */}
        <Button type="button" variant="outline" className="h-9 md:hidden" data-testid="toggle-filters"
          aria-expanded={filtersOpen} onClick={() => setFiltersOpen(o => !o)}>
          <SlidersHorizontal className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />
          Filters{applied !== EMPTY_FILTERS ? " (on)" : ""}
        </Button>
        <div className={`${filtersOpen ? "grid" : "hidden"} order-last w-full grid-cols-1 sm:grid-cols-2 gap-snug md:order-none md:w-auto md:flex md:flex-wrap md:items-end md:gap-base basis-full md:basis-0 md:flex-1 min-w-0 [&_input]:w-full md:[&_input]:w-auto [&_select]:w-full md:[&_select]:w-32`}>
        <Field wide label="Start day & time">
          <DatePicker withTime defaultTime="00:00" testId="filter-start" ariaLabel="Start day"
            value={filters.start} max={filters.end || undefined}
            onChange={next => setFilters(f => ({ ...f, start: next }))} />
        </Field>
        <Field wide label="End day & time">
          <DatePicker withTime defaultTime="23:45" testId="filter-end" ariaLabel="End day"
            value={filters.end} min={filters.start || undefined}
            onChange={next => setFilters(f => ({ ...f, end: next }))} />
        </Field>
        <Field label="Bill / KOT number">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
            <Input data-testid="filter-number" inputMode="numeric" placeholder="Number…" className="h-9 md:h-8 w-36 pl-7 text-body tabular-nums"
              value={filters.number}
              onChange={e => setFilters(f => ({ ...f, number: e.target.value.replace(/\D/g, "") }))} />
          </div>
        </Field>
        <Field label="Table">
          <Input data-testid="filter-table" placeholder="No. / Name" className="h-9 md:h-8 w-24 text-body"
            value={filters.tableNumber}
            onChange={e => setFilters(f => ({ ...f, tableNumber: e.target.value }))} />
        </Field>
        <Field label="Payment type">
          <select data-testid="filter-payment" className="h-9 md:h-8 w-32 rounded-md border border-input bg-background px-2 text-body"
            value={filters.paymentType}
            onChange={e => setFilters(f => ({ ...f, paymentType: e.target.value }))}>
            <option value="">All</option>
            {paymentModes.map(m => (
              <option key={m.id ?? m.name} value={String(m.name).toLowerCase()}>{m.display_label || m.name}</option>
            ))}
          </select>
        </Field>
        <Button type="submit" className="h-9 md:h-8" data-testid="apply-filters">Apply</Button>
        {(applied !== EMPTY_FILTERS || filters !== EMPTY_FILTERS) && (
          <Button type="button" variant="ghost" className="h-9 md:h-8 px-2 text-muted-foreground" onClick={clear} data-testid="clear-filters">
            <X className="h-3.5 w-3.5 mr-1" aria-hidden="true" />Clear
          </Button>
        )}

        </div>

        <div className="ml-auto flex items-end shrink-0">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button type="button" variant="outline" className="h-9 md:h-8" data-testid="export-menu" aria-label="Export history">
                <Download className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />Export
                <ChevronDown className="h-3 w-3 ml-1.5 opacity-60" aria-hidden="true" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[13rem]">
              {[["bills", "Bills"], ["kots", "KOTs"], ["all", "Bills + KOTs"]].map(([kind, label], index) => (
                <div key={kind}>
                  {index > 0 && <DropdownMenuSeparator />}
                  <DropdownMenuLabel className="text-meta font-medium text-muted-foreground">{label}</DropdownMenuLabel>
                  <DropdownMenuItem onSelect={() => doExport(kind, "print")} data-testid={`export-${kind}-print`}>
                    <Printer className="h-3.5 w-3.5 mr-2" aria-hidden="true" />Print
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => doExport(kind, "csv")} data-testid={`export-${kind}-csv`}>
                    <FileText className="h-3.5 w-3.5 mr-2" aria-hidden="true" />CSV
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => doExport(kind, "pdf")} data-testid={`export-${kind}-pdf`}>
                    <Download className="h-3.5 w-3.5 mr-2" aria-hidden="true" />PDF
                  </DropdownMenuItem>
                </div>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </form>

      {/* Tabs — the reference screens' underline grammar, with live counts */}
      <div className="flex flex-wrap items-center justify-between gap-snug border-b shrink-0" role="tablist" data-testid="history-tabs">
        <div className="flex items-center gap-snug overflow-x-auto">
          {TABS.map(([id, label, Icon]) => {
            const active = tab === id;
            const count = id === "orders" ? bills.total : kots.total;
            return (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={active}
                data-testid={`tab-${id}`}
                onClick={() => setTab(id)}
                className={`flex items-center gap-snug px-base py-snug -mb-px text-body font-medium border-b-2 transition-colors ${
                  active
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                <Icon className="w-4 h-4" aria-hidden="true" />
                {label}
                <span className={`text-micro font-bold tabular-nums px-snug py-hair rounded-md ${active ? "bg-primary/10" : "bg-muted"}`}>{count}</span>
              </button>
            );
          })}
        </div>
        <div className="ml-auto sm:ml-0 flex items-center gap-snug text-meta text-muted-foreground tabular-nums" data-testid="history-range">
          {total > 0 && <span>{from}–{to} of {total}</span>}
          {total > PAGE_SIZE && (
            <>
              <Button type="button" variant="ghost" size="icon" className="h-7 w-7" disabled={currentPage === 0 || loading} onClick={() => turnPage(-1)} aria-label="Previous page" data-testid="page-prev">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button type="button" variant="ghost" size="icon" className="h-7 w-7" disabled={to >= total || loading} onClick={() => turnPage(1)} aria-label="Next page" data-testid="page-next">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </>
          )}
        </div>
      </div>

      {/* The ledger */}
      <div className="flex-1 min-h-0 min-w-0 w-full scroll-contain rounded-lg border bg-card">
        {error ? (
          <ErrorState title="Could not load history" message={error} onRetry={load} testId="history-error" />
        ) : loading && !rows.length ? (
          <LoadingState message="Loading history…" testId="history-loading" />
        ) : !rows.length ? (
          tab === "orders"
            ? <EmptyState title="No bills in this window" message="Widen the date range or clear the filters." testId="orders-history-empty" />
            : <EmptyState title="No KOTs in this window" message="Widen the date range or clear the filters." testId="kots-history-empty" />
        ) : tab === "orders" ? (
          <>
          <ul className="md:hidden divide-y" data-testid="orders-history-cards">
            {bills.orders.map(b => (
              <li key={b.id} className="px-base py-snug" data-testid={`bill-card-${b.bill_number}`}>
                <button type="button" className="w-full text-left" onClick={() => setDetailId(b.id)}>
                  <span className="flex items-center justify-between gap-snug">
                    <span className="flex items-center gap-snug min-w-0">
                      <span className="text-lede font-semibold tabular-nums">#{b.bill_number}</span>
                      <StatusBadge status={b.status} />
                    </span>
                    <span className={`${MONEY_LINE} font-semibold`}>{inr(b.total_amount)}</span>
                  </span>
                  <span className="mt-hair flex items-center justify-between gap-snug text-meta text-muted-foreground">
                    <span className="truncate">{b.table_display}{b.customer_name ? `, ${b.customer_name}` : ""}</span>
                    <span className="tabular-nums whitespace-nowrap capitalize">{paymentLabel(b.payment_type)} {fmtTime(b.printed_at)}</span>
                  </span>
                </button>
                <div className="mt-tight flex justify-end">
                  <RowActions id={b.id} busy={busyId === b.id} {...billActions(b)} />
                </div>
              </li>
            ))}
          </ul>
          <table className="hidden md:table w-full text-body" data-testid="orders-history-table">
            <thead className="sticky top-0 z-10 bg-card">
              <tr className="border-b">
                <SortHeader label="Bill Number" field="bill_number" sort={currentSort} onSort={onSort} />
                <Th>Status</Th>
                <Th>Table</Th>
                <Th>Customer Number</Th>
                <Th>Customer Name</Th>
                <Th>Payment Type</Th>
                <Th align="right">Total Amount</Th>
                <SortHeader label="Printed At" field="printed_at" sort={currentSort} onSort={onSort} />
                <SortHeader label="Settled Time" field="settled_time" sort={currentSort} onSort={onSort} />
                <Th align="right">Action</Th>
              </tr>
            </thead>
            <tbody className={loading ? "opacity-60" : ""}>
              {bills.orders.map(b => (
                <tr key={b.id} data-testid={`bill-row-${b.bill_number}`} className="h-9 border-b last:border-0 hover:bg-muted/50">
                  <Td className="font-semibold tabular-nums">{b.bill_number}</Td>
                  <Td><StatusBadge status={b.status} /></Td>
                  <Td className="whitespace-nowrap">{b.table_display}</Td>
                  <Td className="tabular-nums">{b.customer_phone || "—"}</Td>
                  <Td>{b.customer_name || "—"}</Td>
                  <Td className="capitalize">{paymentLabel(b.payment_type)}</Td>
                  <Td align="right" className={MONEY_LINE}>{inr(b.total_amount)}</Td>
                  <Td className="tabular-nums whitespace-nowrap">{fmtTime(b.printed_at)}</Td>
                  <Td className="tabular-nums whitespace-nowrap">{fmtTime(b.settled_time)}</Td>
                  <td className="px-base py-hair">
                    <RowActions id={b.id} busy={busyId === b.id} {...billActions(b)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </>
        ) : (
          <>
          <ul className="md:hidden divide-y" data-testid="kots-history-cards">
            {kots.kots.map(k => {
              const key = `${k.order_id}-${k.kot_number}`;
              return (
                <li key={key} className="px-base py-snug" data-testid={`kot-card-${k.kot_number}`}>
                  <button type="button" className="w-full text-left" onClick={() => setDetailId(k.order_id)}>
                    <span className="flex items-center justify-between gap-snug">
                      <span className="flex items-center gap-snug">
                        <span className="text-lede font-semibold tabular-nums">KOT {k.kot_number}</span>
                        <StatusBadge status={k.status} />
                      </span>
                      <span className="text-meta text-muted-foreground tabular-nums">{fmtTime(k.kot_time)}</span>
                    </span>
                    <span className="mt-hair block text-meta text-muted-foreground truncate">
                      {k.table_display}, {k.captain_name}
                    </span>
                    <span className="mt-hair block text-body truncate">{k.items || "—"}</span>
                  </button>
                  <div className="mt-tight flex justify-end">
                    <RowActions id={k.order_id} busy={busyId === key} onView={() => setDetailId(k.order_id)}
                      onPrint={canReprint ? () => reprint(k) : null}
                      onEdit={isActive(k.status) ? () => openInTill(k) : null} editLabel="Open in till" onDelete={null} />
                  </div>
                </li>
              );
            })}
          </ul>
          <table className="hidden md:table w-full text-body" data-testid="kots-history-table">
            <thead className="sticky top-0 z-10 bg-card">
              <tr className="border-b">
                <SortHeader label="KOT No" field="kot_no" sort={currentSort} onSort={onSort} />
                <Th>Bill Number</Th>
                <Th>Status</Th>
                <Th>Table</Th>
                <Th>Captain Name</Th>
                <Th>Items</Th>
                <SortHeader label="KOT Time" field="kot_time" sort={currentSort} onSort={onSort} />
                <Th align="right">Action</Th>
              </tr>
            </thead>
            <tbody className={loading ? "opacity-60" : ""}>
              {kots.kots.map(k => {
                const key = `${k.order_id}-${k.kot_number}`;
                return (
                  <tr key={key} data-testid={`kot-row-${k.kot_number}`} className="h-9 border-b last:border-0 hover:bg-muted/50">
                    <Td className="font-semibold tabular-nums">{k.kot_number}</Td>
                    <Td className="tabular-nums">{k.bill_number ?? "—"}</Td>
                    <Td><StatusBadge status={k.status} /></Td>
                    <Td className="whitespace-nowrap">{k.table_display}</Td>
                    <Td>{k.captain_name}</Td>
                    <Td className="max-w-[22rem] truncate text-muted-foreground" title={k.items || ""}>{k.items || "—"}</Td>
                    <Td className="tabular-nums whitespace-nowrap">{fmtTime(k.kot_time)}</Td>
                    <td className="px-base py-hair">
                      <RowActions
                        id={k.order_id}
                        busy={busyId === key}
                        onView={() => setDetailId(k.order_id)}
                        onPrint={canReprint ? () => reprint(k) : null}
                        onEdit={isActive(k.status) ? () => openInTill(k) : null}
                        editLabel="Open in till"
                        onDelete={null}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </>
        )}
      </div>

      <OrderDetailSheet
        orderId={detailId}
        token={token}
        canReprint={canReprint}
        onReprint={row => reprint(row)}
        // Owner, 2026-10-24: cashiers and admins by default, per-person switch.
        canChangePayment={featureAllowed("billing.change_payment", user, permissions, ["admin", "cashier"].includes(user?.role))}
        paymentModes={paymentModes}
        onPaymentChanged={load}
        onClose={() => setDetailId(null)}
      />

      <ReopenDialog target={reopenTarget} onCancel={() => setReopenTarget(null)} onConfirm={confirmReopen} />
      <VoidDialog target={voidTarget} onCancel={() => setVoidTarget(null)} onConfirm={confirmVoid} />
    </PageShell>
  );
}

/* ---------------------------------------------------------------- pieces */

const Field = ({ label, children, wide = false }) => (
  <label className={`flex flex-col gap-tight ${wide ? "col-span-2 md:col-span-1" : ""}`}>
    <span className="text-meta font-medium text-muted-foreground">{label}</span>
    {children}
  </label>
);

const TH_CLASS = "px-base py-snug text-meta font-medium text-muted-foreground whitespace-nowrap";

const Th = ({ children, align = "left" }) => (
  <th className={`${TH_CLASS} ${align === "right" ? "text-right" : "text-left"}`}>{children}</th>
);

const Td = ({ children, className = "", align = "left" }) => (
  <td className={`px-base py-hair ${align === "right" ? "text-right" : ""} ${className}`}>{children}</td>
);

function SortHeader({ label, field, sort, onSort }) {
  const activeSort = sort?.by === field;
  const Icon = !activeSort ? ArrowUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <th className={`${TH_CLASS} text-left`}>
      <button
        type="button"
        data-testid={`sort-${field}`}
        onClick={() => onSort(field)}
        aria-sort={activeSort ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
        className={`inline-flex items-center gap-tight hover:text-foreground ${activeSort ? "text-foreground" : ""}`}
      >
        {label}
        <Icon className="h-3 w-3" aria-hidden="true" />
      </button>
    </th>
  );
}

function RowActions({ id, busy, onView, onPrint, onEdit, editLabel, onDelete }) {
  return (
    // Delete sits a step apart from Edit: different consequences, not neighbours
    // (DESIGN.md §3.1; screen review #6).
    <div className="flex items-center justify-end gap-tight md:gap-hair">
      <IconAction testId={`view-${id}`} label="View" onClick={onView} icon={Eye} />
      {onPrint && <IconAction testId={`print-${id}`} label="Print" onClick={onPrint} icon={Printer} disabled={busy} />}
      {onEdit && <IconAction testId={`edit-${id}`} label={editLabel || "Edit"} onClick={onEdit} icon={PencilLine} />}
      {onDelete && <span className="ml-snug flex"><IconAction testId={`delete-${id}`} label="Delete" onClick={onDelete} icon={Trash2} tone="destructive" /></span>}
    </div>
  );
}

function IconAction({ testId, label, onClick, icon: Icon, disabled, tone }) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      // 44px on phones (touch), the 32px desktop icon button from md (DESIGN.md §3).
      className={`h-11 w-11 md:h-8 md:w-8 ${tone === "destructive" ? "text-destructive hover:text-destructive" : "text-muted-foreground hover:text-foreground"}`}
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      data-testid={testId}
    >
      <Icon className="h-5 w-5 md:h-4 md:w-4" aria-hidden="true" />
    </Button>
  );
}

/**
 * The bill, read-only, in a drawer (§5.3: drawers for details). Reads the
 * admin order detail — the same route the till's bill history reads.
 */
function OrderDetailSheet({ orderId, token, canReprint, onReprint, canChangePayment, paymentModes = [], onPaymentChanged, onClose }) {
  const [detail, setDetail] = useState(null);
  const [failure, setFailure] = useState(null);
  const [changing, setChanging] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!orderId) return undefined;
    let live = true;
    setDetail(null);
    setFailure(null);
    history.getClosedOrder(orderId, token)
      .then(payload => { if (live) setDetail(payload); })
      .catch(err => { if (live) setFailure(detailOf(err)); });
    return () => { live = false; };
  }, [orderId, token, reloadKey]);

  const lines = (detail?.items || []).filter(i => i.status !== "cancelled");
  const cancelled = (detail?.items || []).filter(i => i.status === "cancelled");
  const number = detail?.bill_number ?? detail?.order_number;

  return (
    <Sheet open={Boolean(orderId)} onOpenChange={open => { if (!open) onClose(); }}>
      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col gap-base overflow-y-auto" data-testid="order-detail-sheet">
        <SheetHeader className="text-left">
          <SheetTitle className="text-lede font-semibold flex items-center gap-snug">
            {detail ? <>Bill #{number} {detail.status && <StatusBadge status={detail.status} />}</> : "Bill"}
          </SheetTitle>
          <SheetDescription className="text-meta">
            {detail
              ? [
                  detail.table ? (detail.table.label || `Table ${detail.table.number}`) : null,
                  detail.customer?.name || detail.customer?.phone || null,
                  `Opened ${fmtTime(detail.created_at)}`,
                  detail.closed_at ? `Settled ${fmtTime(detail.closed_at)}` : null,
                ].filter(Boolean).join(" · ")
              : "Loading the bill…"}
          </SheetDescription>
        </SheetHeader>

        {failure && <ErrorState density="inline" title="Could not load the bill" message={failure} testId="order-detail-error" />}
        {!failure && !detail && <LoadingState density="inline" message="Loading the bill…" testId="order-detail-loading" />}

        {detail && (
          <>
            <table className="w-full text-body" data-testid="order-detail-lines">
              <thead>
                <tr className="border-b">
                  <th className={`${TH_CLASS} text-left`}>Item</th>
                  <th className={`${TH_CLASS} text-right`}>Qty</th>
                  <th className={`${TH_CLASS} text-right`}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {lines.map(i => (
                  <tr key={i.id} className="border-b last:border-0">
                    <td className="px-snug py-tight">
                      {i.item_name}
                      {i.kot_number ? <span className="ml-snug text-micro text-muted-foreground">KOT {i.kot_number}</span> : null}
                      {i.is_nc ? <span className="ml-snug text-meta font-medium text-muted-foreground">NC</span> : null}
                    </td>
                    <td className="px-snug py-tight text-right tabular-nums">{fmtQty(i.quantity)}</td>
                    <td className={`px-snug py-tight text-right ${MONEY_LINE}`}>{inr(i.line_total ?? Number(i.unit_price) * Number(i.quantity))}</td>
                  </tr>
                ))}
                {cancelled.map(i => (
                  <tr key={i.id} className="border-b last:border-0 text-muted-foreground line-through">
                    <td className="px-snug py-tight">{i.item_name}</td>
                    <td className="px-snug py-tight text-right tabular-nums">{fmtQty(i.quantity)}</td>
                    <td className="px-snug py-tight text-right tabular-nums">—</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <dl className="grid grid-cols-[1fr_auto] gap-x-base gap-y-hair text-body" data-testid="order-detail-money">
              <dt className="text-muted-foreground">Subtotal</dt><dd className={`text-right ${MONEY_LINE}`}>{inr(detail.subtotal)}</dd>
              {Number(detail.discount_amount) > 0 && (
                <><dt className="text-muted-foreground">Discount{detail.discount_reason ? ` · ${detail.discount_reason}` : ""}</dt><dd className={`text-right ${MONEY_LINE}`}>−{inr(detail.discount_amount)}</dd></>
              )}
              {Number(detail.service_charge) > 0 && (
                <><dt className="text-muted-foreground">Service charge</dt><dd className={`text-right ${MONEY_LINE}`}>{inr(detail.service_charge)}</dd></>
              )}
              <dt className="text-muted-foreground">Tax</dt><dd className={`text-right ${MONEY_LINE}`}>{inr(detail.tax_amount)}</dd>
              <dt className="font-semibold">Total</dt><dd className={`text-right ${MONEY_LINE} font-bold`}>{inr(detail.total)}</dd>
              <dt className="text-muted-foreground">Payment</dt><dd className="text-right capitalize">{paymentLabel(detail.payment_method)}</dd>
            </dl>

            <OrderActivity orderId={detail.id} token={token} refreshKey={reloadKey} />

            {canReprint && detail.status !== "cancelled" && (
              <Button type="button" variant="outline" className="h-9 self-start" onClick={() => onReprint({ id: detail.id })} data-testid="order-detail-reprint">
                <Printer className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />Print bill
              </Button>
            )}
            {canChangePayment && detail.status === "closed" && paymentModes.length > 0 && (
              <Button type="button" variant="outline" className="h-9 self-start" onClick={() => setChanging(true)} data-testid="order-detail-change-payment">
                Change payment type
              </Button>
            )}
            {changing && (
              <ChangePaymentDialog
                bill={detail}
                modes={paymentModes}
                token={token}
                onClose={() => setChanging(false)}
                onChanged={() => { setReloadKey(k => k + 1); onPaymentChanged?.(); }}
              />
            )}
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

function ReopenDialog({ target, onCancel, onConfirm }) {
  const number = target?.bill_number ?? target?.order_number;
  return (
    <AlertDialog open={Boolean(target)} onOpenChange={open => { if (!open) onCancel(); }}>
      <AlertDialogContent data-testid="reopen-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Reopen bill #{number} for editing?</AlertDialogTitle>
          <AlertDialogDescription className="text-meta">
            This is an admin override: the bill goes back to the floor as an open order and is
            audited as reopened. Its bill number does not change. Settle it again when the edit is done.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="reopen-cancel">Keep as is</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} data-testid="reopen-confirm">Reopen</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function VoidDialog({ target, onCancel, onConfirm }) {
  const [reason, setReason] = useState("");
  useEffect(() => { if (target) setReason(""); }, [target]);
  const ready = reason.trim().length >= 3;
  return (
    <AlertDialog open={Boolean(target)} onOpenChange={open => { if (!open) onCancel(); }}>
      <AlertDialogContent data-testid="void-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Delete bill #{target?.bill_number}?</AlertDialogTitle>
          <AlertDialogDescription className="text-meta">
            The bill leaves the day's sales and its table, if any, is freed. Bill number {target?.bill_number} stays
            retired — it is never given to another order. The reason goes on the audit trail.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <label className="flex flex-col gap-tight">
          <span className="text-meta font-medium text-muted-foreground">Reason</span>
          <Input
            value={reason}
            onChange={event => setReason(event.target.value)}
            placeholder="Why this bill is being deleted"
            autoFocus
            data-testid="void-reason"
          />
        </label>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="void-cancel">Keep the bill</AlertDialogCancel>
          <AlertDialogAction
            disabled={!ready}
            onClick={() => ready && onConfirm(reason.trim())}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            data-testid="void-confirm"
          >
            Delete bill
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
